import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Selection } from '@atlaskit/editor-prosemirror/state';
import {
	DEFAULT_BLOCK_LINK_HASH_PREFIX,
	extractBlockIdFromLinkHash,
} from '@atlaskit/editor-common/block-menu/block-link';
import { expandAllParentsThenScroll } from '@atlaskit/editor-common/block-menu/scroll-to-block-utils';
import { getLocalIdSelector } from '@atlaskit/editor-common/block-menu';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset/builder';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { scrollEditorHeadingIntoView } from '../utils/scrollEditorHeadingIntoView.ts';

export type EditorPresetAPI = ExtractPresetAPI<any>;

export const handleSelectionOnSyncedBlock = (
	editorAPI: EditorPresetAPI | undefined,
	blockId: string,
): void => {
	if (!editorAPI?.core?.actions?.execute) {
		return;
	}

	// Catch the following potential errors when trying to set selection on the synced block:
	// tr.doc.resolve(...) - Can throw throw a RangeError
	// tr.setSelection(...) - Can throw
	try {
		editorAPI.core.actions.execute(({ tr }) => {
			const doc = tr.doc;

			// Find first top-level node matching the block ID and of type 'bodiedSyncBlock'.
			let syncedBlockPos = -1;

			doc.descendants((node, pos, parent) => {
				if (
					syncedBlockPos < 0 && // already found the block or not a top-level node, skip further checks
					parent?.type.name === 'doc' &&
					node.attrs?.localId === blockId &&
					node.type?.name === 'bodiedSyncBlock'
				) {
					syncedBlockPos = pos;
				}

				// Don't descend into this node's children
				// as synced blocks cannot be nested and we only want the first match at the top level.
				return false;
			});

			if (syncedBlockPos >= 0) {
				const safePos = Math.max(0, Math.min(syncedBlockPos + 1, tr.doc.content.size));
				// Set selection to the first position inside the synced block node.
				const $pos = tr.doc.resolve(safePos);
				editorAPI.core?.actions?.focus();
				tr.setSelection(Selection.near($pos));
				return tr;
			}

			return null;
		});
	} catch {
		// Silently fail if selection cannot be set
	}
};

export const useScrollOnUrlChange = (
	hasProseMirrorRendered: boolean,
	blockLinkHashPrefix: string = DEFAULT_BLOCK_LINK_HASH_PREFIX,
	editorAPI?: EditorPresetAPI, // Editor API for setting selection on synced blocks
): void => {
	const { hash } = useLocation();

	useEffect(() => {
		if (!hasProseMirrorRendered || !hash) {
			return;
		}

		// Store cleanup function to cancel pending operations on unmount or hash change.
		let cancelExpandAndScroll: (() => void) | null = null;
		let selectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

		// Handle block links when block menu feature is enabled.
		if (expValEquals('platform_editor_block_menu', 'isEnabled', true)) {
			const blockId = extractBlockIdFromLinkHash(hash, blockLinkHashPrefix);
			if (blockId) {
				const element = getLocalIdSelector(blockId, document.body);
				if (element) {
					// Handle block links with expand support.
					// Capture cleanup function to cancel pending timeouts on unmount/hash change.
					cancelExpandAndScroll = expandAllParentsThenScroll(element, 0, (el) => {
						el.scrollIntoView({ behavior: 'smooth', block: 'start' });
					});

					// Set selection on synced block if editor API is available
					// Small delay to ensure scroll/expand completes before setting selection
					if (expValEquals('platform_synced_block', 'isEnabled', true)) {
						selectionTimeoutId = setTimeout(() => {
							handleSelectionOnSyncedBlock(editorAPI, blockId);
						}, 100); // Delay to ensure scroll completes
					}
				}
				// Return cleanup function to cancel pending operations.
				return () => {
					if (cancelExpandAndScroll) {
						cancelExpandAndScroll();
					}
					if (selectionTimeoutId) {
						clearTimeout(selectionTimeoutId);
					}
				};
			}
		}

		// Handle regular anchor links and heading links.
		const id = hash.slice(1);

		const anchorElement = document.getElementById(id);
		// Indicates anchor.
		if (anchorElement) {
			anchorElement.scrollIntoView();
		}

		cancelExpandAndScroll = scrollEditorHeadingIntoView(id);
		if (cancelExpandAndScroll) {
			return () => {
				cancelExpandAndScroll?.();
			};
		}
	}, [hash, blockLinkHashPrefix, hasProseMirrorRendered, editorAPI]);
};
